/**
 * IncidentHistoryPage — AUDIT-INCIDENT-HISTORY PRD v1 S1: incident history
 * lookup + filter. Every value shown comes from the DB (provenance fields are
 * passthrough from the incident's formData as written at promotion);
 * `investigationStatus` is the server's documented derivation. Missing values
 * render "—" — never invented.
 *
 * S4 design pass (docs/audits/s4-design-pass-2026-10-15.md §2B/§3/§4): the
 * fetch outcome lives in ONE discriminated union (`ListState`) instead of
 * four independent states — denied⊕error⊕rows combinations are
 * unrepresentable. Exactly ONE effect (`[filters, page]`) starts requests and
 * bumps the generation; handlers only set filters/page. Refetch clears to
 * loading (owner decision D2 — no stale-while-revalidate). 403 is detected
 * by response status ONLY (never body text, never a client role guess).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, FileDown } from 'lucide-react';
import { incidentApi, type IncidentHistoryFilters } from '../utils/api';
import { toCsv, downloadCsv, type CsvColumn } from '../utils/csvExport';
// S4 design pass §4: date parsing/formatting lives ONLY in utils/utcDay.ts;
// the page re-exports the total formatter so existing spec imports keep
// working — one formatter, two consumers (table cell + CSV), never split.
import { formatUtcDay } from '../utils/utcDay';
export { formatUtcDay };

const INCIDENT_TYPES = [
  'PERSONAL_INJURY', 'VEHICLE_ACCIDENT', 'PROPERTY_DAMAGE', 'THEFT', 'NEAR_MISS',
  'ENVIRONMENTAL_SPILL', 'EQUIPMENT_FAILURE', 'SECURITY_INCIDENT', 'THIRD_PARTY_INCIDENT',
];
const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const INVESTIGATION_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'ROOT_CAUSE_IDENTIFIED'];

interface HistoryRow {
  id: number;
  type: string;
  severity: string;
  date: string | null;
  description: string;
  projectName: string | null;
  reporterName: string | null;
  investigatorName: string | null;
  publicId: string | null;
  locationAddress: string | null;
  openActionCount: number;
  actionItemCount: number;
  investigationStatus: string;
}

function normalizeRows(res: any): HistoryRow[] {
  const rows = res?.data?.data ?? res?.data ?? res;
  return Array.isArray(rows) ? rows : [];
}

// The ONE fetch-outcome state: exactly one status renders at a time.
type ListState =
  | { status: 'loading' }
  | { status: 'ready'; rows: HistoryRow[]; total: number }
  | { status: 'empty'; total: number }
  | { status: 'error'; message: string }
  | { status: 'denied' };

export function IncidentHistoryPage() {
  // S4: drill-down seeding — History & Reports trend cards deep-link here
  // with ?type=&severity=&dateFrom=&dateTo= (only params the filter panel
  // accepts; unknown params ignored — no fabricated filters).
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<IncidentHistoryFilters>(() => {
    const seeded: IncidentHistoryFilters = {};
    const allowed: Array<keyof IncidentHistoryFilters> = ['type', 'severity', 'dateFrom', 'dateTo', 'q', 'investigationStatus'];
    for (const key of allowed) {
      const v = searchParams.get(key);
      if (v) (seeded as Record<string, string>)[key] = v;
    }
    return seeded;
  });
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [page, setPage] = useState(1);
  const limit = 20;

  // S4: the generation is invalidated SYNCHRONOUSLY in the ONE request
  // starter (this function, called only by the single [filters, page]
  // effect), and BOTH settle paths check it — an older in-flight request
  // resolving after a newer one must never overwrite the newer state.
  // Handlers never call this directly and never touch genRef (§4).
  const genRef = useRef(0);

  const load = useCallback(async (f: IncidentHistoryFilters, p: number) => {
    genRef.current += 1;
    const gen = genRef.current;
    setList({ status: 'loading' }); // D2: previous rows/error/denied are DROPPED
    try {
      const res = await incidentApi.getAll({ ...f, page: p, limit });
      if (gen !== genRef.current) return; // stale — NO-OP
      const rows = normalizeRows(res);
      const total = res?.data?.pagination?.total ?? res?.pagination?.total ?? 0;
      setList(rows.length > 0 ? { status: 'ready', rows, total } : { status: 'empty', total });
    } catch (e: any) {
      if (gen !== genRef.current) return; // stale — NO-OP
      // Server-decided access: a 403 (by response STATUS only) means this
      // account lacks the incident.view permission — explain it in place of
      // the table instead of a generic error. Never body text, never a
      // client role guess.
      if (e?.response?.status === 403) {
        setList({ status: 'denied' });
        return;
      }
      setList({
        status: 'error',
        message: e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Could not load incident history.',
      });
    }
  }, []);

  // The sole fetch trigger: mount, filter change, page change, and remount
  // (a new instance starts fresh at gen 0→1; the old instance's in-flight
  // settles die with its refs — no cross-instance leak).
  useEffect(() => {
    void load(filters, page);
  }, [filters, page, load]);

  const applyFilter = (patch: Partial<IncidentHistoryFilters>) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    setPage(1);
  };

  const cell = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? '—' : String(v));

  // S4: CSV export of the currently LOADED rows only — the button is
  // disabled unless the list is ready/empty (it must never export the
  // previous filter's rows) and the title states that honesty. Date-only
  // field renders through the SAME formatUtcDay as the table cell (one
  // formatter, two consumers); provenance cells are the raw DB values
  // ("—" is a UI convention only).
  const INCIDENT_CSV_COLUMNS: Array<CsvColumn<HistoryRow>> = [
    { key: 'publicId', label: 'Report ID' },
    { key: 'date', label: 'Date (UTC day)' },
    { key: 'type', label: 'Type' },
    { key: 'severity', label: 'Severity' },
    { key: 'description', label: 'Description' },
    { key: 'projectName', label: 'Project' },
    { key: 'reporterName', label: 'Reporter' },
    { key: 'investigatorName', label: 'Investigator' },
    { key: 'locationAddress', label: 'Location' },
    { key: 'openActionCount', label: 'Open actions' },
    { key: 'actionItemCount', label: 'Total actions' },
    { key: 'investigationStatus', label: 'Investigation status' },
  ];
  const exportableRows = list.status === 'ready' ? list.rows : [];
  const exportEnabled = list.status === 'ready' || list.status === 'empty';
  const handleExportCsv = () => {
    const csvRows = exportableRows.map((r) => ({ ...r, date: formatUtcDay(r.date) }));
    downloadCsv('incident-history.csv', toCsv(INCIDENT_CSV_COLUMNS, csvRows));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Incident history</h1>
        <p className="text-sm text-gray-500 mb-6 flex items-center gap-3 flex-wrap">
          <span>Search and filter your company's incident record. Values are shown exactly as recorded.</span>
          <Link to="/history" className="text-blue-600 hover:underline">History & Reports</Link>
          <button
            onClick={handleExportCsv}
            disabled={!exportEnabled}
            title="Exports the currently loaded rows"
            aria-label="Export the currently loaded incident rows as CSV"
            className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <FileDown className="h-4 w-4" /> Export CSV
          </button>
        </p>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="text-sm text-gray-600">
            Type
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5"
              value={filters.type ?? ''}
              onChange={(e) => applyFilter({ type: e.target.value || undefined })}
            >
              <option value="">All types</option>
              {INCIDENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Severity
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5"
              value={filters.severity ?? ''}
              onChange={(e) => applyFilter({ severity: e.target.value || undefined })}
            >
              <option value="">All severities</option>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Investigation status
            <select
              className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5"
              value={filters.investigationStatus ?? ''}
              onChange={(e) => applyFilter({ investigationStatus: e.target.value || undefined })}
            >
              <option value="">Any status</option>
              {INVESTIGATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Search descriptions
            <div className="mt-1 flex items-center gap-2">
              <input
                className="w-full border border-gray-300 rounded-lg px-2 py-1.5"
                placeholder="e.g. cut, fall, rebar"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyFilter({ q: search.trim() || undefined });
                }}
              />
              <button
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                onClick={() => applyFilter({ q: search.trim() || undefined })}
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
              </button>
            </div>
          </label>
          <label className="text-sm text-gray-600">
            From date
            <input
              type="date"
              className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5"
              value={filters.dateFrom ?? ''}
              onChange={(e) => applyFilter({ dateFrom: e.target.value || undefined })}
            />
          </label>
          <label className="text-sm text-gray-600">
            To date
            <input
              type="date"
              className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5"
              value={filters.dateTo ?? ''}
              onChange={(e) => applyFilter({ dateTo: e.target.value || undefined })}
            />
          </label>
          <label className="text-sm text-gray-600 flex items-end gap-2 pb-1.5">
            <input
              type="checkbox"
              checked={filters.hasOpenActions === true}
              onChange={(e) => applyFilter({ hasOpenActions: e.target.checked ? true : undefined })}
            />
            Only incidents with open actions
          </label>
        </div>

        {list.status === 'error' && <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 text-red-700">{list.message}</div>}

        {list.status === 'denied' && (
          <div role="alert" className="mb-4 p-4 rounded-lg text-sm bg-amber-50 border border-amber-300 text-amber-900 max-w-2xl">
            <strong>Incident list restricted.</strong> The row-level incident history is limited to accounts holding the
            incident.view permission — Read-Only accounts only see it when an administrator has granted that
            permission to them company-wide. To get access, ask an administrator to grant the incident.view permission
            to your account. Until then, the Reports tab still shows incident summaries for your company.
          </div>
        )}

        {/* Results — hidden entirely while access is denied (the alert replaces the table) */}
        {list.status !== 'denied' && (
        <div className="bg-white rounded-xl shadow border border-gray-100 overflow-x-auto">
          {list.status === 'loading' && <p className="p-4 text-sm text-gray-500">Loading incident history…</p>}
          {list.status === 'empty' && (
            <p className="p-4 text-sm text-gray-600">No incidents match the current filters.</p>
          )}
          {list.status === 'ready' && (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Severity</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2">Project</th>
                  <th className="px-4 py-2">Reporter</th>
                  <th className="px-4 py-2">Investigator</th>
                  <th className="px-4 py-2">Open actions</th>
                  <th className="px-4 py-2">Investigation</th>
                  <th className="px-4 py-2">Report</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap">
                      {/* S4: UTC calendar day via the single owner (utils/utcDay.ts)
                          — must match the API's UTC-day filter semantics, never a
                          local-tz day shift. Missing or unparseable dates render
                          '—' — never invented. */}
                      {formatUtcDay(r.date)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.type}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.severity}</td>
                    <td className="px-4 py-2 max-w-xs truncate" title={r.description}>{r.description}</td>
                    <td className="px-4 py-2">{cell(r.projectName)}</td>
                    <td className="px-4 py-2">{cell(r.reporterName)}</td>
                    <td className="px-4 py-2">{cell(r.investigatorName)}</td>
                    <td className="px-4 py-2">{r.openActionCount}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.investigationStatus}</td>
                    <td className="px-4 py-2">
                      <Link to={`/investigations/${r.id}`} className="text-blue-600 hover:underline">
                        {cell(r.publicId)}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}

        {/* Pagination */}
        {(list.status === 'ready' || list.status === 'empty') && list.total > limit && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <button
              className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>Page {page} of {Math.max(1, Math.ceil(list.total / limit))} ({list.total} incidents)</span>
            <button
              className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-50"
              disabled={page >= Math.ceil(list.total / limit)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default IncidentHistoryPage;
